import { CreateGlobalOpeningHoursExceptionRequest } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '../../shared/DateTimePicker/DateTimePicker';
import { InlineErrorMessage } from '../../shared/InlineErrorMessage/InlineErrorMessage';
import { CustomSelect, SelectOption } from '../../shared/CustomSelect/CustomSelect';
import { objectKeys } from '../../shared/utils/typeUtils';
import { createHoursMap, hourTypeLabel, openingHoursDefaults } from '../SingleRestaurantPage/tabs/Hours/utils';
import { TimePicker } from '../../shared/TimePicker/TimePicker';
import { useEffect, useState } from 'react';
import { FormSwitch } from '../../shared/FormSwitch/FormSwitch';
import { useSaveGlobalExceptionHours } from '../../apis/shops-api';
import { ExceptionItem } from './GlobalExceptionHoursPage';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  item: ExceptionItem | null;
};

type GlobalExceptionHoursForm = CreateGlobalOpeningHoursExceptionRequest & {
  isClosed: boolean;
};

const GlobalExceptionHoursModal: React.FC<Props> = ({ isOpen, onClose, item }) => {
  const [isTimeDisabled, setTimeDisabled] = useState(false);

  const {
    reset,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<GlobalExceptionHoursForm>({
    defaultValues: item
      ? item
      : {
          isClosed: false,
        },
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (item) {
      reset(item);
    } else {
      reset({ isClosed: false });
    }
  }, [item, reset]);

  const handleClose = () => {
    reset({ isClosed: false });
    onClose();
  };

  const saveHours = useSaveGlobalExceptionHours({ onSettled: handleClose });

  const handleForm: SubmitHandler<GlobalExceptionHoursForm> = (formData) => {
    if (formData.isClosed) {
      formData.startTime = openingHoursDefaults.closedTime;
      formData.endTime = openingHoursDefaults.closedTime;
    }

    saveHours.mutate({ id: item?.id, request: formData });
  };

  const isClosed = watch('isClosed');

  useEffect(() => {
    if (isClosed) {
      setTimeDisabled(true);
    } else {
      setTimeDisabled(false);
    }
  }, [isClosed]);

  const hoursMap = createHoursMap();

  const typeValues: SelectOption[] = objectKeys(hoursMap).map((type) => ({
    title: hourTypeLabel(type),
    value: type,
  }));

  return (
    <Dialog open={isOpen} keepMounted maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle>{item ? t('globalExceptions.modal.editTitle') : t('globalExceptions.modal.addTitle')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(handleForm)}>
          <Box sx={{ my: 2 }}>
            <DateTimePicker
              dateOnly
              rules={{ required: t('global.fieldIsRequired') }}
              control={control}
              label={t('exceptionDate.list.date')}
              name="date"
            />
            <InlineErrorMessage>{errors['date']?.message}</InlineErrorMessage>
          </Box>
          <Box sx={{ my: 2 }}>
            <CustomSelect
              label={t('restaurant.hours.exceptions.type.label')}
              showEmptyOption={false}
              isLoading={false}
              rules={{ required: t('global.fieldIsRequired') }}
              options={typeValues}
              control={control}
              name="type"
              sx={{ width: '100%' }}
            />
            <InlineErrorMessage>{errors['type']?.message}</InlineErrorMessage>
          </Box>
          <Box sx={{ my: 2 }}>
            <TimePicker
              rules={{ required: isTimeDisabled ? false : t('global.fieldIsRequired') }}
              control={control}
              label={t('restaurant.hours.exceptions.starts.label')}
              name="startTime"
              disabled={isTimeDisabled}
            />
            <InlineErrorMessage>{errors['startTime']?.message}</InlineErrorMessage>
          </Box>
          <Box sx={{ my: 2 }}>
            <TimePicker
              rules={{ required: isTimeDisabled ? false : t('global.fieldIsRequired') }}
              control={control}
              label={t('restaurant.hours.exceptions.ends.label')}
              name="endTime"
              disabled={isTimeDisabled}
            />
            <InlineErrorMessage>{errors['endTime']?.message}</InlineErrorMessage>
          </Box>
          <Box sx={{ my: 2 }}>
            <FormSwitch name="isClosed" control={control} label={t('Closed')} />
          </Box>
          <DialogActions>
            <Button variant="outlined" color="error" onClick={handleClose} disabled={saveHours.isLoading}>
              {t('global.cancel')}
            </Button>
            <Button variant="contained" color="success" type="submit" disabled={saveHours.isLoading}>
              {t('global.save')}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalExceptionHoursModal;
